import React, { useState, useEffect } from 'react';
import { tenantAdminAPI } from '../../../services/api/tenantAdminAPI';
import useEscKeyHandler from '../../../hooks/useEscKeyHandler';
import toast from 'react-hot-toast';
import { 
  Users, 
  Plus, 
  Search, 
  Edit3, 
  Trash2, 
  Eye,
  CheckCircle,
  XCircle,
  Mail,
  Phone,
  Calendar,
  UserCheck,
  Shield,
  Activity,
  Key
} from 'lucide-react';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // 'view', 'create', 'edit'
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Role mapping for consistent display
  const roleDisplayNames = {
    'tenant_doctor': 'Clinician',
    'tenant_nurse': 'Nurse'
  };

  // Get current tenant ID from auth context (you'll need to implement this)
  const getCurrentTenantId = () => {
    // This should get the current tenant ID from your auth context
    // For now, we'll get it from the first user's tenant_id as a fallback
    if (users.length > 0 && users[0].tenant_id) {
      return users[0].tenant_id;
    }
    return null; // Will be handled by backend
  };

  useEffect(() => {
    loadUsers();
  }, [filterStatus]);

  useEffect(() => {
    filterUsers();
  }, [searchTerm, filterRole, filterStatus, users]);

  // Add Esc key handler for modal
  useEscKeyHandler(showModal, () => setShowModal(false));

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await tenantAdminAPI.getUsers({
        status: filterStatus === '' ? undefined : filterStatus
      });
      // Handle different response structures
      const userData = response.data?.users || response.data || [];
      setUsers(Array.isArray(userData) ? userData : []);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
      setUsers([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    // Ensure users is always an array
    const userArray = Array.isArray(users) ? users : [];
    let filtered = userArray;

    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterRole) {
      filtered = filtered.filter(user => user.role === filterRole);
    }

    if (filterStatus) {
      filtered = filtered.filter(user => user.status === filterStatus);
    }

    setFilteredUsers(filtered);
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setModalMode('create');
    setShowModal(true);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setModalMode('view');
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        const response = await tenantAdminAPI.deleteUser(userId);
        
        // Check if the response indicates success
        if (response.status === 200 || response.status === 204) {
          toast.success('User deleted successfully');
          // Update the user status to inactive in local state instead of removing
          setUsers(prevUsers => prevUsers.map(user => 
            user.id === userId ? { ...user, status: 'inactive' } : user
          ));
          setFilteredUsers(prevFilteredUsers => prevFilteredUsers.map(user => 
            user.id === userId ? { ...user, status: 'inactive' } : user
          ));
        } else {
          throw new Error('Unexpected response status');
        }
      } catch (error) {
        console.error('Error deleting user:', error);
        const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete user';
        toast.error(errorMessage);
      }
    }
  };

  const handleRestoreUser = async (userId) => {
    if (window.confirm('Are you sure you want to restore this user?')) {
      try {
        const response = await tenantAdminAPI.updateUser(userId, { status: 'active' });
        
        if (response.status === 200) {
          toast.success('User restored successfully');
          // Update the user status in local state
          setUsers(prevUsers => prevUsers.map(user => 
            user.id === userId ? { ...user, status: 'active' } : user
          ));
          setFilteredUsers(prevFilteredUsers => prevFilteredUsers.map(user => 
            user.id === userId ? { ...user, status: 'active' } : user
          ));
        } else {
          throw new Error('Unexpected response status');
        }
      } catch (error) {
        console.error('Error restoring user:', error);
        const errorMessage = error.response?.data?.detail || error.message || 'Failed to restore user';
        toast.error(errorMessage);
      }
    }
  };

  // Add MFA and Password Setup functions
  const handleSendMfaEnrollment = async (user) => {
    try {
      console.log(`=== FRONTEND: Sending MFA enrollment for user: ${user.email} ===`);
      toast.loading('Sending MFA enrollment email...', { id: 'mfa-enrollment' });
      
      const response = await tenantAdminAPI.sendMfaEnrollment(user.id);
      
      if (response.data.success) {
        toast.success(`MFA enrollment email sent to ${user.email}`, { id: 'mfa-enrollment' });
        console.log('MFA enrollment email sent successfully:', response.data);
      } else {
        throw new Error(response.data.message || 'Failed to send MFA enrollment email');
      }
    } catch (error) {
      console.error('Error sending MFA enrollment email:', error);
      toast.error(
        error.response?.data?.detail || 
        error.message || 
        'Failed to send MFA enrollment email', 
        { id: 'mfa-enrollment' }
      );
    }
  };

  const handleResendPasswordSetup = async (user) => {
    try {
      console.log(`=== FRONTEND: Resending password setup for user: ${user.email} ===`);
      toast.loading('Resending password setup email...', { id: 'password-resend' });
      
      const response = await tenantAdminAPI.resendPasswordSetup(user.id);
      
      if (response.data.success) {
        toast.success(`Password setup email resent to ${user.email}`, { id: 'password-resend' });
        console.log('Password setup email resent successfully:', response.data);
      } else {
        throw new Error(response.data.message || 'Failed to resend password setup email');
      }
    } catch (error) {
      console.error('Error resending password setup email:', error);
      toast.error(
        error.response?.data?.detail || 
        error.message || 
        'Failed to resend password setup email', 
        { id: 'password-resend' }
      );
    }
  };

  const UserModal = () => {
    const [formData, setFormData] = useState({
      firstName: '',
      lastName: '',
      email: '',
      role: 'tenant_doctor',
      phone: '',
      status: 'active'
    });

    useEffect(() => {
      if (selectedUser && (modalMode === 'edit' || modalMode === 'view')) {
        const nameParts = (selectedUser.name || '').split(' ');
        setFormData({
          firstName: nameParts[0] || '',
          lastName: nameParts.slice(1).join(' ') || '',
          email: selectedUser.email || '',
          role: selectedUser.role || 'tenant_doctor',
          phone: selectedUser.phone || '',
          status: selectedUser.status || 'active'
        });
      }
    }, [selectedUser, modalMode]);

    const handleSubmit = async (e) => {
      e.preventDefault();
      
      // Prevent duplicate submissions
      if (isSubmitting) {
        return;
      }
      
      setIsSubmitting(true);
      try {
        if (modalMode === 'create') {
          // Use tenantAdminAPI for full Auth0 flow
          const createData = {
            first_name: formData.firstName,
            last_name: formData.lastName,
            email: formData.email,
            role: formData.role,
            phone_number: formData.phone
          };
          
          const result = await tenantAdminAPI.createUserWithAuth0(createData);
          toast.success(result.data?.message || 'User created successfully with password setup and MFA enrollment emails sent');
        } else if (modalMode === 'edit') {
          // Keep existing edit logic
          const userData = {
            ...formData,
            name: `${formData.firstName} ${formData.lastName}`.trim()
          };
          await tenantAdminAPI.updateUser(selectedUser.id, userData);
          toast.success('User updated successfully');
        }
        
        setShowModal(false);
        loadUsers();
      } catch (error) {
        console.error(`Error ${modalMode}ing user:`, error);
        toast.error(`Failed to ${modalMode} user`);
      } finally {
        setIsSubmitting(false);
      }
    };

    const isReadOnly = modalMode === 'view';

    const roleOptions = [
      { value: 'tenant_doctor', label: 'Clinician' },
      { value: 'tenant_nurse', label: 'Nurse' }
    ];

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-[#F9FBFC] rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-[#DCEAF5]">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-[#0B1C2C]">
                {modalMode === 'create' ? 'Create New User' : 
                 modalMode === 'edit' ? 'Edit User' : 'User Details'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-[#3D3D3D] hover:text-[#207CE5]"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                  required
                  readOnly={isReadOnly}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                  required
                  readOnly={isReadOnly}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                  required
                  readOnly={isReadOnly}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                  Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                  disabled={isReadOnly}
                  required
                >
                  {roleOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                  readOnly={isReadOnly}
                />
              </div>

              {modalMode === 'edit' && (
                <div>
                  <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              )}
              
              {modalMode === 'edit' && formData.status === 'inactive' && (
                <div className="col-span-2">
                  <button
                    type="button"
                    onClick={() => handleRestoreUser(selectedUser.id)}
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-[#008080] border border-transparent rounded-lg hover:bg-[#006666] transition-colors"
                  >
                    <UserCheck className="h-4 w-4 inline mr-2" />
                    Restore User
                  </button>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-[#DCEAF5]">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-[#3D3D3D] bg-[#F9FBFC] border border-[#DCEAF5] rounded-lg hover:bg-[#DCEAF5]"
              >
                {isReadOnly ? 'Close' : 'Cancel'}
              </button>
              {!isReadOnly && (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-4 py-2 text-sm font-medium text-white bg-[#207CE5] border border-transparent rounded-lg ${
                    isSubmitting 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'hover:bg-[#4682B4]'
                  }`}
                >
                  {isSubmitting ? 'Creating...' : (modalMode === 'create' ? 'Create User' : 'Update User')}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#207CE5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0B1C2C]">User Management</h1>
          <p className="text-[#3D3D3D]">Manage users within your organization</p>
        </div>
        <button
          onClick={handleCreateUser}
          className="flex items-center space-x-2 px-4 py-2 bg-[#207CE5] text-white rounded-lg hover:bg-[#4682B4] transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Add New User</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-[#F9FBFC] rounded-xl shadow-sm border border-[#DCEAF5] p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#3D3D3D]" />
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
            />
          </div>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-4 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CEAF5]"
          >
            <option value="">All Roles</option>
            <option value="tenant_doctor">Clinician</option>
            <option value="tenant_nurse">Nurse</option>
            
            {/* <option value="tenant_patient">Patient</option> */}
          </select>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
          >
            <option value="active">Active Users</option>
            <option value="inactive">Inactive Users</option>
          </select>
          
          {/* <button
            onClick={() => setFilterStatus(filterStatus === 'active' ? '' : 'active')}
            className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
              filterStatus === 'active' 
                ? 'bg-[#207CE5] text-white border-[#207CE5]' 
                : 'bg-white text-[#207CE5] border-[#207CE5] hover:bg-[#F0F8FF]'
            }`}
            title={filterStatus === 'active' ? 'Show All Users' : 'Show Only Active Users'}
          >
            {filterStatus === 'active' ? 'Show All' : 'Active Only'}
          </button> */}
        </div>
      </div>

      {/* Users Table */}
      
      <div className="bg-[#F9FBFC] rounded-xl shadow-sm border border-[#DCEAF5]">
        <div className="overflow-x-auto">
          {/* Table Header with Filter Info */}
          {/* <div className="px-4 py-3 bg-[#DCEAF5] border-b border-[#DCEAF5]">
            <div className="flex items-center justify-between">
              <div className="text-sm text-[#3D3D3D]">
                {filterStatus === 'active' && 'Showing Active Users Only'}
                {filterStatus === 'inactive' && 'Showing Inactive Users Only'}
                {filterStatus === '' && 'Showing All Users'}
                {filterRole && ` • Filtered by Role: ${filterRole.replace('tenant_', '').charAt(0).toUpperCase() + filterRole.replace('tenant_', '').slice(1)}`}
              </div>
              <div className="text-sm text-[#3D3D3D]">
                Total: {filteredUsers.length} users
              </div>
            </div>
          </div>   */}
          <table className="w-full min-w-full">
            <thead className="bg-[#DCEAF5]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#3D3D3D] uppercase tracking-wider w-1/3">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#3D3D3D] uppercase tracking-wider w-1/6">
                  Role
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#3D3D3D] uppercase tracking-wider w-1/6">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#3D3D3D] uppercase tracking-wider w-1/6">
                  Created
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#3D3D3D] uppercase tracking-wider w-1/6">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#F9FBFC] divide-y divide-[#DCEAF5]">
              {filteredUsers.map((user) => {
                // Comment out patient viewing - hide patients from the table
                if (user.role === 'tenant_patient') {
                  return null;
                }
                
                return (
                <tr key={user.id} className="hover:bg-[#DCEAF5]">
                  <td className="px-4 py-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#207CE5] to-[#4682B4] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">
                          {user.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div className="ml-3 min-w-0 flex-1">
                        <div className="text-sm font-medium text-[#0B1C2C] truncate">{user.name}</div>
                        <div className="text-sm text-[#3D3D3D] truncate">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.role === 'tenant_doctor' ? 'bg-[#DCEAF5] text-[#207CE5]' :
                      user.role === 'tenant_nurse' ? 'bg-[#DCEAF5] text-[#008080]' :
                      // user.role === 'tenant_technician' ? 'bg-[#DCEAF5] text-[#00BCD4]' :
                      'bg-[#DCEAF5] text-[#3D3D3D]'
                    }`}>
                      {roleDisplayNames[user.role] || user.role.replace('tenant_', '').charAt(0).toUpperCase() + user.role.replace('tenant_', '').slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center">
                      {user.status === 'active' ? (
                        <CheckCircle className="h-5 w-5 text-[#008080] flex-shrink-0" />
                      ) : (
                        <XCircle className="h-5 w-5 text-[#00BCD4] flex-shrink-0" />
                      )}
                      <span className={`ml-2 text-sm ${
                        user.status === 'active' ? 'text-[#008080]' : 'text-[#00BCD4]'
                      }`}>
                        {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-[#3D3D3D]">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-4 py-4 text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleSendMfaEnrollment(user)}
                        className="text-gray-400 hover:text-green-600"
                        title="Send MFA Enrollment"
                      >
                        {/* <Key className="h-4 w-4" /> */}
                      </button>
                      <button
                        onClick={() => handleResendPasswordSetup(user)}
                        className="text-gray-400 hover:text-blue-600"
                        title="Resend Password Setup"
                      >
                        {/* <Mail className="h-4 w-4" /> */}
                      </button>
                      <button
                        onClick={() => handleViewUser(user)}
                        className="text-[#3D3D3D] hover:text-[#207CE5] p-1"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditUser(user)}
                        className="text-[#3D3D3D] hover:text-[#207CE5] p-1"
                        title="Edit"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      {user.status === 'active' ? (
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-[#3D3D3D] hover:text-[#00BCD4] p-1"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleRestoreUser(user.id)}
                          className="text-[#3D3D3D] hover:text-[#008080] p-1"
                          title="Restore User"
                        >
                          <UserCheck className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {filteredUsers.length === 0 && (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-[#3D3D3D] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[#0B1C2C] mb-2">No users found</h3>
          <p className="text-[#3D3D3D]">
            {searchTerm ? 'Try adjusting your search criteria' : 'Get started by adding your first user'}
          </p>
        </div>
      )}

      {/* Modal */}
      {showModal && <UserModal />}
    </div>
  );
};

export default UserManagement;